import { useState, useRef, useEffect } from "react";
import { getCloudflareImageUrl } from "../utils/imageOptimization";
import { EditableImageSVG } from "./SVG";
import Spinner from "./Spinner";

const ImageOptionsMenu = ({ onImageChange, imageType, position }) => {
  const fileInputRef = useRef(null);

  return (
    <div
      className="fixed bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 z-50 p-2 transform translate-x-10"
      style={{
        left: `${position.left}px`,
        top: `${position.top}px`,
      }}
    >
      <div className="flex flex-col space-y-2">
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex space-x-2 px-2 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg w-full"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
          <span>Dodaj sliku</span>
          <input
            ref={fileInputRef}
            type="file"
            name={imageType}
            className="hidden"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files?.[0]) {
                onImageChange(e);
              }
            }}
          />
        </button>

        <button
          onClick={() => {
            onImageChange({ target: { name: imageType, value: "" } });
          }}
          className="flex space-x-2 px-2 py-2 text-sm text-red-600 hover:bg-gray-100 rounded-lg w-full"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          <span>Ukloni sliku</span>
        </button>
      </div>
    </div>
  );
};

const EditableImage = ({
  imageUrl,
  defaultImage,
  onImageChange,
  isEditable = false,
  children,
  className = "",
  imageType,
  setIsUploading,
  isUploading,
  handleAlert,
  domain,
  includeImage = true,
  texture,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const finalImageUrl = imageUrl || defaultImage;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showMenu &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    const fieldName = e.target.name;
    const includeImageFieldName =
      "include" +
      String(fieldName[0]).toUpperCase() +
      String(fieldName).slice(1);

    if (e.target.value === "") {
      onImageChange({ [fieldName]: "", [includeImageFieldName]: false });
      return;
    }

    if (!file) return;

    setIsUploading(true);

    try {
      const imageUrl = await uploadImageToS3(file);
      onImageChange({ [fieldName]: imageUrl, [includeImageFieldName]: true });
    } catch (error) {
      handleAlert("Failed to upload image. Please try again.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const uploadImageToS3 = async (file) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();

    const fileName = `${crypto.randomUUID()}`;

    const presignedResponse = await fetch(`${domain}/generate-presigned-url`, {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fileName: fileName,
        fileType: file.type,
      }),
    });

    if (!presignedResponse.ok) {
      throw new Error("Failed to get presigned URL");
    }

    const { url: presignedUrl, publicUrl } = await presignedResponse.json();

    const uploadResponse = await fetch(presignedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error("Failed to upload image to S3");
    }

    return publicUrl;
  };

  return (
    <div className={`relative ${className} ${texture ? "p-8" : ""}`}>
      {isUploading && <Spinner />}
      {includeImage && (
        <picture>
          <source
            media="(max-width: 640px)"
            srcSet={getCloudflareImageUrl(
              finalImageUrl,
              "width=640,quality=80",
            )}
          />
          <source
            media="(max-width: 1024px)"
            srcSet={getCloudflareImageUrl(
              finalImageUrl,
              "width=1024,quality=80",
            )}
          />
          <source
            media="(min-width: 1025px)"
            srcSet={getCloudflareImageUrl(
              finalImageUrl,
              "width=1920,quality=80",
            )}
          />
          <img
            src={
              getCloudflareImageUrl(finalImageUrl, "width=1024,quality=80") ||
              finalImageUrl
            }
            alt="Background Image"
            className={`w-full h-auto ${texture ? "rounded-2xl" : ""}`}
          />
        </picture>
      )}
      {children}
      {isEditable && (
        <div
          className={`absolute ${
            includeImage
              ? "top-4"
              : imageType === "countdownImage"
              ? "-top-8"
              : ""
          } right-4 z-[100]`}
          ref={menuRef}
        >
          <button
            ref={buttonRef}
            onClick={() => {
              if (!buttonRef.current) return;
              const rect = buttonRef.current.getBoundingClientRect();
              setMenuPosition({
                top: rect.bottom + 8,
                left: window.innerWidth / 2,
              });
              setShowMenu(!showMenu);
            }}
            className="p-2 rounded-full shadow-lg bg-teal-600 text-gray-200 hover:text-white z-50 pointer-events-auto"
            title="Image options"
          >
            <EditableImageSVG />
          </button>
          {showMenu && (
            <ImageOptionsMenu
              onImageChange={handleImageChange}
              onClose={() => setShowMenu(false)}
              imageType={imageType}
              position={menuPosition}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default EditableImage;
